/**
 * InfoPanel.ts — the "i" sheet a regulator (and a careful player) expects.
 *
 * Surfaces every fact the player needs to make an informed decision:
 *   • RTP %, volatility class, max-win cap (the audit blocker — these were hidden).
 *   • Full PAYTABLE — drip / color-complete / picture-complete weighted multipliers.
 *   • Provably-fair: current serverSeedHash + clientSeed + nonce, with a "verify a bet" link.
 *   • Responsible-gambling links (BeGambleAware, GamCare) + entry point to the limits panel.
 *
 * One scene-level overlay; tap the "i" affordance in the HUD to mount it.
 */
import { Container, Graphics, Text } from 'pixi.js';
import theme from '../render/theme';
import { Button, makeText } from './widgets';
import type { Viewport } from '../app/AppContext';
import type { MathConfig } from '../net/contract';
import { mult as fmtMult } from '../util/format';

interface PFView {
  serverSeedHash: string;
  clientSeed: string;
  nonce: number;
  mathVersion: string;
}

export class InfoPanel extends Container {
  private bg = new Graphics();
  private card = new Graphics();
  private close: Button;
  private content = new Container();
  private opts: { onClose: () => void; onOpenLimits: () => void };

  constructor(opts: { onClose: () => void; onOpenLimits: () => void }) {
    super();
    this.opts = opts;
    this.eventMode = 'static';
    this.bg.eventMode = 'static';
    this.bg.on('pointertap', () => this.opts.onClose());
    this.addChild(this.bg, this.card, this.content);

    this.close = new Button({
      width: 44, height: 44, label: '✕', fontSize: 22, radius: 22,
      fill: theme.color.white, textColor: theme.color.ink,
      onTap: () => this.opts.onClose(),
    });
    this.addChild(this.close);
  }

  /** (re)render with the latest math config + provably-fair snapshot. */
  render(cfg: MathConfig, pf: PFView): void {
    this.content.removeChildren();
    const lines: Container[] = [];

    // header
    lines.push(this.row(
      makeText('Game info', { size: 22, weight: '800', color: theme.color.ink }),
    ));
    lines.push(this.row(
      makeText('Theoretical RTP', { size: 14, weight: '700', color: theme.color.muted }),
      makeText(`${(cfg.rtp * 100).toFixed(2)}%`, { size: 16, weight: '800', color: theme.color.teal }),
    ));
    lines.push(this.row(
      makeText('Volatility', { size: 14, weight: '700', color: theme.color.muted }),
      makeText('Mid (fat-tail picture bonus)', { size: 14, weight: '700', color: theme.color.ink }),
    ));
    // max win = highest picture-complete multiplier × theoretical worst-case stake
    const maxMult = Math.max(...cfg.pictureCompleteDisplay.map((r) => r.mult));
    lines.push(this.row(
      makeText('Max win', { size: 14, weight: '700', color: theme.color.muted }),
      makeText(`${fmtMult(maxMult)} of total stake`, { size: 14, weight: '800', color: theme.color.gold2 }),
    ));
    lines.push(this.row(
      makeText('Math version', { size: 14, weight: '700', color: theme.color.muted }),
      makeText(pf.mathVersion.slice(0, 12) + '…', { size: 12, weight: '700', color: theme.color.muted }),
    ));

    lines.push(this.spacer(14));
    lines.push(this.row(makeText('Paytable', { size: 16, weight: '800', color: theme.color.ink })));

    lines.push(this.row(makeText('Drip per cell  (× stake)', { size: 13, weight: '700', color: theme.color.muted })));
    for (const r of cfg.dripDisplay) {
      lines.push(this.row(
        makeText(`${fmtMult(r.mult)}`, { size: 13, weight: '800', color: theme.color.ink }),
        makeText(`${r.weightPct.toFixed(1)}%`, { size: 13, weight: '700', color: theme.color.muted }),
      ));
    }
    lines.push(this.spacer(8));
    lines.push(this.row(makeText('Color complete  (× stake)', { size: 13, weight: '700', color: theme.color.muted })));
    for (const r of cfg.colorCompleteDisplay) {
      lines.push(this.row(
        makeText(`${fmtMult(r.mult)}`, { size: 13, weight: '800', color: theme.color.ink }),
        makeText(`${r.weightPct.toFixed(1)}%`, { size: 13, weight: '700', color: theme.color.muted }),
      ));
    }
    lines.push(this.spacer(8));
    lines.push(this.row(makeText('Picture complete  (× total stake, full picture only)', { size: 13, weight: '700', color: theme.color.muted })));
    for (const r of cfg.pictureCompleteDisplay) {
      lines.push(this.row(
        makeText(`${fmtMult(r.mult)}`, { size: 13, weight: '800', color: theme.color.ink }),
        makeText(`${r.weightPct.toFixed(2)}%`, { size: 13, weight: '700', color: theme.color.muted }),
      ));
    }

    lines.push(this.spacer(14));
    lines.push(this.row(makeText('Provably fair', { size: 16, weight: '800', color: theme.color.ink })));
    lines.push(this.row(
      makeText('Server seed hash', { size: 12, weight: '700', color: theme.color.muted }),
      makeText(pf.serverSeedHash.slice(0, 16) + '…', { size: 12, weight: '700', color: theme.color.ink }),
    ));
    lines.push(this.row(
      makeText('Client seed', { size: 12, weight: '700', color: theme.color.muted }),
      makeText(pf.clientSeed.slice(0, 16) + (pf.clientSeed.length > 16 ? '…' : ''), { size: 12, weight: '700', color: theme.color.ink }),
    ));
    lines.push(this.row(
      makeText('Nonce', { size: 12, weight: '700', color: theme.color.muted }),
      makeText(String(pf.nonce), { size: 12, weight: '700', color: theme.color.ink }),
    ));

    lines.push(this.spacer(14));
    lines.push(this.row(makeText('Play responsibly', { size: 16, weight: '800', color: theme.color.ink })));
    const helpLine = new Container();
    const txt = makeText('Help & limits: BeGambleAware.org · GamCare.org.uk', {
      size: 12, weight: '700', color: theme.color.purple,
    });
    helpLine.addChild(txt);
    lines.push(helpLine);

    let y = 14;
    for (const ln of lines) {
      ln.position.set(20, y);
      this.content.addChild(ln);
      y += (ln as Container & { _h?: number })._h ?? 26;
    }
    (this.content as Container & { _y?: number })._y = y;

    // bottom action: open RG limits sheet
    const limitsBtn = new Button({
      width: 200, height: 44, label: 'Set my limits', radius: 22,
      fill: theme.color.teal, textColor: theme.color.white, fontSize: 15,
      onTap: () => this.opts.onOpenLimits(),
    });
    limitsBtn.position.set(20, y + 6);
    (limitsBtn as Container & { _isLimits?: boolean })._isLimits = true;
    this.content.addChild(limitsBtn);
  }

  private row(...children: (Text | Container)[]): Container {
    const c = new Container();
    let x = 0;
    let h = 0;
    for (const ch of children) {
      ch.position.x = x;
      c.addChild(ch);
      x += 'width' in ch ? (ch as { width: number }).width + 12 : 0;
      h = Math.max(h, 'height' in ch ? (ch as { height: number }).height : 18);
    }
    (c as Container & { _h?: number })._h = h + 6;
    return c;
  }

  private spacer(h: number): Container {
    const c = new Container();
    (c as Container & { _h?: number })._h = h;
    return c;
  }

  layout(v: Viewport): void {
    const w = Math.min(v.width - 24, 420);
    const h = Math.min(v.height - 80, 640);
    this.bg.clear();
    this.bg.rect(0, 0, v.width, v.height).fill({ color: 0x0d0820, alpha: 0.6 });
    const x = (v.width - w) / 2;
    const y = (v.height - h) / 2;
    this.card.clear();
    this.card.roundRect(x, y, w, h, theme.radius.card).fill({ color: theme.color.white, alpha: 1 });
    this.card.roundRect(x, y, w, h, theme.radius.card).stroke({ width: 1, color: theme.color.hint, alpha: 0.4 });
    this.content.position.set(x, y);
    this.close.position.set(x + w - 56, y + 12);
  }
}
